/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import { useLocation, useNavigate } from 'js/lib/useRouter';

import type SSOError from 'bundles/authentication-common/components/sso/SSOError';
import { AUTHENTICATION_MODAL_MODES } from 'bundles/authentication-common/constants';
import type { AUTHENTICATION_MODAL_MODES_KEY } from 'bundles/authentication-common/constants';
import { AuthModalTrackerContextProvider } from 'bundles/authentication-common/contexts/AuthenticationTracking';
import * as actions from 'bundles/authentication/shared/actions';
import type { OnAuthenticationOptions } from 'bundles/authentication/shared/actions';
import AuthenticationContentTracker from 'bundles/authentication/shared/components/authentication-modal/AuthenticationContentTracker';
import CompleteSSO from 'bundles/authentication/shared/components/authentication-modal/CompleteSSO';
import ExpiredLink from 'bundles/authentication/shared/components/authentication-modal/ExpiredLink';
import ForgotPassword from 'bundles/authentication/shared/components/authentication-modal/ForgotPassword';
import LinkedAccount from 'bundles/authentication/shared/components/authentication-modal/LinkedAccount';
import Login from 'bundles/authentication/shared/components/authentication-modal/Login';
import MagicLink from 'bundles/authentication/shared/components/authentication-modal/MagicLink';
import OrganizationSSOLogin from 'bundles/authentication/shared/components/authentication-modal/OrganizationSSO/OrganizationSSOLogin';
import OrganizationSSOSignup from 'bundles/authentication/shared/components/authentication-modal/OrganizationSSO/OrganizationSSOSignup';
import RequestMagicLink from 'bundles/authentication/shared/components/authentication-modal/RequestMagicLink';
import SSO from 'bundles/authentication/shared/components/authentication-modal/SSO';
import Signup from 'bundles/authentication/shared/components/authentication-modal/Signup';
import UnifiedAuthEntry from 'bundles/authentication/shared/components/authentication-modal/UnifiedAuthEntry';
import useEnterpriseSsoEmailCheck from 'bundles/authentication/shared/hooks/useEnterpriseSsoCheck';
import useForceSSOLoginQuery from 'bundles/authentication/shared/hooks/useForceSSOLoginQuery';
import useUserThirdPartyOrganizationsQuery from 'bundles/authentication/shared/hooks/useUserThirdPartyOrganizationsQuery';
import useWhitelistOrgForSSO from 'bundles/authentication/shared/hooks/useWhitelistOrgForSSO';
import { isTermsOfUseRolloutForUS } from 'bundles/authentication/utils';
import { getModalTrackerValue } from 'bundles/authentication/utils/eventingUtils';
import type { AuthenticationContentBaseProps } from 'bundles/user-account/types';

type Context = Record<string, any>;

export type Props = AuthenticationContentBaseProps & {
  context: Context;
  isStandaloneAuth?: boolean;
  hideOrganizationSSO?: boolean;
};

const getAuthViews: (showProgressiveAuth: boolean) => Record<AUTHENTICATION_MODAL_MODES_KEY, any> = (
  showProgressiveAuth
) => ({
  complete: CompleteSSO,
  error: Login,
  expired: ExpiredLink,
  forgot: ForgotPassword,
  linked: LinkedAccount,
  login: showProgressiveAuth ? UnifiedAuthEntry : Login,
  logincontinue: Login,
  magiclink: MagicLink,
  organizationlogin: OrganizationSSOLogin,
  organizationsignup: OrganizationSSOSignup,
  requestmagiclink: RequestMagicLink,
  signup: showProgressiveAuth ? UnifiedAuthEntry : Signup,
  signupcontinue: Signup,
  sso: SSO,
});

const styles = {
  container: css`
    max-height: 100%;
    height: 100vh;
    overflow: auto;
    position: relative;

    @media (min-width: 600px) {
      height: auto;
    }
  `,
};

const AuthenticationContent = ({
  context: contextFromProps,
  defaultMode = AUTHENTICATION_MODAL_MODES.LOGIN,
  isStandaloneAuth,
  redirectTo,
  magicLinkRedirect,
  preventRegister,
  switchFormsByQueryParam = true,
  hideOrganizationSSO = false,
  programId,
}: Props) => {
  const [context, setContext] = useState<Context>(contextFromProps ?? {});
  const [ssoError, setSsoError] = useState<SSOError | undefined>();

  useEffect(() => {
    setContext((prevContext = {}) => ({ ...prevContext, ...contextFromProps }));
  }, [contextFromProps]);

  const [mode, setMode] = useState<AUTHENTICATION_MODAL_MODES_KEY>(defaultMode);
  const location = useLocation();
  const navigate = useNavigate();
  const { authProvider, showProgressiveAuth } = context;

  const { organizations, loading: isThirdPartyLoading } = useUserThirdPartyOrganizationsQuery(authProvider);

  const organizationId = organizations?.[0]?.id;
  const { forceSSOLogin, loading: isForceSSOLoading } = useForceSSOLoginQuery(organizationId || '');
  const { isOrgOrProgramWhiteListedForSSOLogin, isLoading: isWhitelistOrgForSSOLoginLoading } = useWhitelistOrgForSSO({
    thirdPartyOrganizationId: organizationId,
    programId,
  });

  // add epic variable for XDPs CRO
  const onNavigate = (
    mode: AUTHENTICATION_MODAL_MODES_KEY,
    options: Context = {},
    shouldSwitchFormsByQueryParam = switchFormsByQueryParam
  ) => {
    const areModesTheSame = location.pathname.includes(`/${mode}`);
    if (
      isStandaloneAuth &&
      !areModesTheSame &&
      (mode === AUTHENTICATION_MODAL_MODES.SIGNUP || mode === AUTHENTICATION_MODAL_MODES.LOGIN)
    ) {
      navigate(`/${mode}${location.search}`, { replace: true });
    } else {
      if (shouldSwitchFormsByQueryParam) {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('authMode', mode);
        navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
      }
      setContext((prevContext) => ({ ...prevContext, ...options }));
      setMode(mode);
    }
  };

  const onLogin = (redirectURL?: string) => {
    actions.onLogin(redirectTo || redirectURL);
  };

  const onSignup = (redirectURL?: string, options?: OnAuthenticationOptions) => {
    actions.onSignup(redirectTo || redirectURL, options);
  };

  const onSSOError = (error: SSOError) => {
    const { code } = error;

    switch (code) {
      case 'enforceSsoV2':
        // For enforceSsoV2 - SSO v2, where we enforce enterprise sso login, we just set the error state and don't navigate
        return setSsoError(error);
      case 'accountLinkedToSocialAccount':
        return onNavigate(AUTHENTICATION_MODAL_MODES.LINKED, { accountType: error.response?.existingAccountType });
      case 'existingCourseraAccount':
      case 'noEmailAvailable':
      default:
        return onNavigate(AUTHENTICATION_MODAL_MODES.COMPLETE, {
          mode: code,
          referrer: mode,
          service: error.service,
        });
    }
  };

  const resetSsoError = () => {
    setSsoError(undefined);
  };

  const enterpriseSsoEmailCheckInfo = useEnterpriseSsoEmailCheck();

  const onSSOSuccess = (isRegistration: boolean, hasSavedOnboardingOnSignup?: boolean) => {
    if (isRegistration) {
      onSignup(undefined, { addShowTouAcceptParam: isTermsOfUseRolloutForUS(), hasSavedOnboardingOnSignup });
    } else {
      onLogin();
    }
  };

  const ssoContext = {
    forceSSOLogin,
    organizations,
    shouldShowCustomSSO: isOrgOrProgramWhiteListedForSSOLogin,
    areAnyApiRequestsInFlight: isThirdPartyLoading || isForceSSOLoading || isWhitelistOrgForSSOLoginLoading,
    ssoError,
    resetSsoError,
    enterpriseSsoEmailCheckInfo,
  };

  const Content = (getAuthViews(showProgressiveAuth)[mode] || Login) as unknown as React.ComponentType<any>;
  return (
    <section css={styles.container} className="authentication-box-content">
      <AuthModalTrackerContextProvider
        value={getModalTrackerValue({
          mode,
          isProgressiveAuth: showProgressiveAuth,
          formVariant: context.formVariant,
          socialSsoService: context.thirdPartyAuthService,
        })}
      >
        <AuthenticationContentTracker />
        <Content
          redirect={redirectTo}
          magicLinkRedirect={magicLinkRedirect}
          navigate={onNavigate}
          onLogin={onLogin}
          onSignup={onSignup}
          onSSOError={onSSOError}
          onSSOSuccess={onSSOSuccess}
          preventRegister={preventRegister}
          hideOrganizationSSO={hideOrganizationSSO}
          {...ssoContext}
          {...context}
        />
      </AuthModalTrackerContextProvider>
    </section>
  );
};

export default AuthenticationContent;
